import { useEffect, useState } from "react";
import { WifiOff, Wifi, CloudUpload } from "lucide-react";
import { subscribe as subscribeQueue, flush as flushQueue } from "../utils/offlineQueue";

/**
 * Shows a subtle top banner when the browser reports no connection, and a
 * quick "reconnected" confirmation for 2.5s when coming back online.
 * When writes have been queued, the banner surfaces the pending count so
 * users can see their practice is safe.
 */
const OfflineIndicator = () => {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [justReconnected, setJustReconnected] = useState(false);
  const [queueSize, setQueueSize] = useState(0);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      setJustReconnected(true);
      // Kick the flush — the queue module also auto-flushes, but this
      // makes the banner transition feel instant.
      flushQueue().catch(() => {});
      setTimeout(() => setJustReconnected(false), 2500);
    };
    const handleOffline = () => setOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    const unsub = subscribeQueue(setQueueSize);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      unsub();
    };
  }, []);

  // Nothing to show while online + no pending writes + not in the 2.5s
  // reconnected window.
  if (online && !justReconnected && queueSize === 0) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-0 inset-x-0 z-[60] pointer-events-none flex justify-center pt-2 px-3 transition-all duration-300"
      data-testid={online ? "reconnected-banner" : "offline-banner"}
    >
      <div
        className={`pointer-events-auto flex items-center gap-2 px-4 py-2 rounded-full shadow-lg backdrop-blur-md text-sm font-medium animate-fade-in ${
          online
            ? "bg-emerald-500/95 text-white"
            : "bg-destructive/95 text-destructive-foreground"
        }`}
      >
        {online ? (
          <>
            <Wifi className="h-4 w-4" />
            {queueSize > 0 ? (
              <>
                <CloudUpload className="h-4 w-4" />
                Syncing {queueSize} saved {queueSize === 1 ? "entry" : "entries"}…
              </>
            ) : (
              <>Back online — syncing your practice</>
            )}
          </>
        ) : (
          <>
            <WifiOff className="h-4 w-4" />
            You're offline — viewing cached content
            {queueSize > 0 && (
              <span
                className="ml-2 inline-flex items-center gap-1 bg-white/15 px-2 py-0.5 rounded-full text-xs"
                data-testid="offline-queue-count"
              >
                <CloudUpload className="h-3 w-3" />
                {queueSize} pending
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OfflineIndicator;
